// src/features/admin/AdminSubSidebar.jsx
//
// Slice Adm1: left-column section nav for the AdminPage. 200px wide,
// stacked icon + label rows, persisted activeKey owned by the parent.
//
// Visual language mirrors a sidebar (same family as NavRail) —
// inline SVG icons via `<AdminSubSidebarIcon>` (see ADR in that file's
// header). Active row uses white bg + 0.5px border + blue icon tint;
// inactive rows are muted with no background.
//
// ADMIN-NAV-EXPAND (2026-06-11): each section object now carries an
// optional `group` field. When the group changes between consecutive
// items the sidebar renders an uppercase group-label row before the
// first item of that group (9.5px, letter-spaced, T.light style —
// matches the comp).
//
// Pure component: no internal state, no localStorage, no side effects.
// Persistence + fallback live in AdminPage so this is straightforward
// to test in isolation.

import React from "react";
import { T } from "../../app/tokens.js";
import { AdminSubSidebarIcon } from "../../components/AdminSubSidebarIcon.jsx";

const WRAPPER = {
  width: 200,
  background: T.bg,
  borderRight: `0.5px solid ${T.border}`,
  padding: "8px 8px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  flexShrink: 0,
};

const GROUP_LABEL = {
  fontSize: T.fsXxxs5,
  fontWeight: T.fwBold,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: T.light,
  padding: "10px 10px 4px",
  userSelect: "none",
};

function rowStyle(isActive) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 10px",
    borderRadius: 6,
    background: isActive ? T.white : "transparent",
    border: isActive ? `0.5px solid ${T.border}` : "0.5px solid transparent",
    color: isActive ? T.dark : T.mid,
    cursor: "pointer",
    fontSize: T.fsBase,
    fontWeight: isActive ? 600 : 500,
    textAlign: "left",
    width: "100%",
    fontFamily: "inherit",
    outline: "none",
  };
}

function iconStyle(isActive) {
  return {
    color: isActive ? T.blue : T.light,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

/**
 * @param {object} props
 * @param {Array<{ key: string, label: string, group?: string }>} props.sections
 * @param {string} props.activeKey
 * @param {(key: string) => void} props.onSelect
 * @param {Set<string>} [props.dimmedKeys] - Preview-mode-only: keys that are
 *   hidden by an admin_section toggle but shown dimmed so admins can see
 *   what a real (non-admin) view would hide once preview mode is off.
 */
export function AdminSubSidebar({ sections, activeKey, onSelect, dimmedKeys }) {
  let lastGroup = null;

  return (
    <div data-testid="admin-sub-sidebar" style={WRAPPER}>
      {sections.map((section) => {
        const showGroupLabel = section.group && section.group !== lastGroup;
        if (section.group) lastGroup = section.group;

        const isActive = section.key === activeKey;
        const isDimmed = dimmedKeys?.has(section.key) ?? false;
        return (
          <React.Fragment key={section.key}>
            {showGroupLabel && (
              <div
                data-testid={`admin-group-label-${section.group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={GROUP_LABEL}
              >
                {section.group}
              </div>
            )}
            <button
              type="button"
              data-testid={`admin-section-${section.key}`}
              data-active={isActive ? "true" : "false"}
              data-preview-hidden={isDimmed ? "true" : undefined}
              aria-current={isActive ? "page" : undefined}
              title={isDimmed ? "Hidden from non-admins - visible to you because preview mode is on." : undefined}
              onClick={() => onSelect(section.key)}
              style={{ ...rowStyle(isActive), opacity: isDimmed ? 0.5 : 1 }}
            >
              <span style={iconStyle(isActive)}>
                <AdminSubSidebarIcon itemKey={section.key} size={18} />
              </span>
              <span>{section.label}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}
